import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CalendarDaysIcon, ClockIcon, MonitorPlayIcon } from 'lucide-react';
import { initMetaPixel, trackPageView } from '../utils/metaPixel';
import { trackPage } from '../utils/analytics';
import { initOpenAIPixel } from '../utils/openaiPixel';
import { MentorSection } from '../components/MentorSection';
const GHL_FORM_ID = 'RezDIwpAnYWI1Kza4xcb';
const GHL_FORM_HOST = 'https://api.webfyre.com';
const BACKGROUND_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/e89f4a2f-a61e-44df-8e8e-f6d119d2105d.jpg';
const LEARNINGS = ['Cómo pasar de un solo ingreso a un sistema de varias fuentes que no dependen de tu tiempo.', 'Los tres vehículos de Bienes Raíces de cómo puedes generar ingresos.', 'La ruta exacta para iniciar y cómo expandir tus resultados en Bienes Raíces.'];
const EVENT_FACTS = [{
  icon: CalendarDaysIcon,
  label: 'Fecha',
  value: '6 de Octubre, 2026'
}, {
  icon: ClockIcon,
  label: 'Hora',
  value: '7:30pm (CDMX)'
}, {
  icon: MonitorPlayIcon,
  label: 'Modalidad',
  value: 'Online en vivo'
}];
interface ClaseFBProps {
  /** Path recorded in analytics so each traffic source is measured separately. */
  trackingPath?: string;
  /** Loads the OpenAI ads pixel (used on the ChatGPT traffic variant). */
  enableOpenAIPixel?: boolean;
  /** Loads the Meta (Facebook) pixel and fires PageView. */
  enableMetaPixel?: boolean;
  /** Webfyre form embedded on this variant. */
  formId?: string;
}
export function ClaseFB({
  trackingPath = '/clase-fb',
  enableOpenAIPixel = false,
  enableMetaPixel = true,
  formId = GHL_FORM_ID
}: ClaseFBProps) {
  useEffect(() => {
    if (enableMetaPixel) {
      initMetaPixel();
      trackPageView();
    }
    if (enableOpenAIPixel) initOpenAIPixel();
    trackPage(trackingPath);
  }, [trackingPath, enableOpenAIPixel, enableMetaPixel]);

  // Load GoHighLevel form script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `${GHL_FORM_HOST}/js/form_embed.js`;
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  return <div className="relative min-h-screen bg-black font-sans text-white flex flex-col" data-id="element-307" data-mp="130uzcta47yce">
      {/* BACKGROUND */}
      <div className="pointer-events-none fixed inset-0 bg-cover bg-center" style={{
      backgroundImage: `url(${BACKGROUND_IMAGE})`
    }} aria-hidden="true" data-id="element-308" data-mp="y29ycrn1hamw" />
      <div className="pointer-events-none fixed inset-0 bg-black/80" aria-hidden="true" data-id="element-309" data-mp="1qpxe9tdd7d37" />
      {/* DATE STRIP */}
      <div className="sticky top-0 z-50 w-full bg-[#E5A82E] text-black shadow-md" data-id="element-310" data-mp="1gr89916khn4d">
        <p className="px-4 py-2.5 text-center text-xs sm:text-sm font-bold uppercase tracking-wide" data-id="element-311" data-mp="1gtayvk3t5e8k">
          Clase en vivo de Bienes Raíces · 6 de Octubre, 2026 — 7:30pm (Hora
          CDMX) · Cupo limitado
        </p>
      </div>

      {/* SPLIT SCREEN */}
      <main className="relative z-10 flex-1 w-full grid grid-cols-1 md:grid-cols-2 md:content-center" data-id="element-312" data-mp="2h0pr1ujt4rut">
        {/* TITLE — left column, top on mobile */}
        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }} className="order-1 md:col-start-1 md:row-start-1 px-5 pt-5 pb-3 sm:px-8 md:pt-14 md:pb-0 lg:px-12 xl:px-16" data-id="element-313" data-mp="2qxkp6cc64q8k">
          <p className="mb-2 md:mb-3 text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-gold-metallic" data-id="element-314" data-mp="39obun2t1bwho">
            Clase Gratuita en Vivo
          </p>

          <h1 className="font-sans text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold uppercase leading-[1.08]" data-id="element-315" data-mp="3el2kuiyk08gt">
            Como Construir Múltiples Fuentes de Ingreso en Bienes Raíces
          </h1>
        </motion.section>

        {/* CLASS DETAILS — left column, after the form on mobile */}
        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="order-3 md:col-start-1 md:row-start-2 px-5 pt-8 pb-10 sm:px-8 md:pt-6 md:pb-14 lg:px-12 xl:px-16" data-id="element-316" data-mp="3asc9flgj52dg">
          <p className="text-base lg:text-lg text-white/70 leading-relaxed mb-8" data-id="element-317" data-mp="2n0x3zjtgi4xz">
            <span className="font-semibold text-white" data-id="element-318" data-mp="30s1v1xirdowp">Susana Sánchez G</span>{' '}
            te muestra el modelo para iniciar desde cero en Bienes Raíces y cómo
            construir fuentes adicionales en esta industria.
          </p>

          <ul className="space-y-4 mb-10" data-id="element-319" data-mp="28api0u48q3sj">
            {LEARNINGS.map(item => <li key={item} className="flex gap-3 text-white/80" data-id="element-320" data-mp="24ap5jf0pc2o4">
                <span className="mt-2 h-1.5 w-6 flex-shrink-0 bg-gold-metallic" aria-hidden="true" data-id="element-321" data-mp="248cnl1uy1sb2" />
                <span className="leading-relaxed" data-id="element-322" data-mp="1yy0fgtyk0n9e">{item}</span>
              </li>)}
          </ul>

        </motion.section>

        {/* RIGHT HALF — FORM */}
        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: [0.23, 1, 0.32, 1]
      }} id="registro-clase" className="order-2 md:col-start-2 md:row-start-1 md:row-span-2 flex flex-col justify-center bg-black/60 backdrop-blur-sm border-b border-white/10 md:border-b-0 md:border-l px-5 pt-3 pb-10 sm:px-8 md:py-14 lg:px-12 xl:px-16" aria-label="Registro a la clase" data-id="element-323" data-mp="1p6w1nxkm78g5">
          <dl className="w-full max-w-md mx-auto mb-4 md:mb-6 grid grid-cols-3 gap-3 border-b border-white/10 pb-4 md:pb-6" data-id="element-324" data-mp="wjjgnru3r8oy">
            {EVENT_FACTS.map(({
            icon: Icon,
            label,
            value
          }) => <div key={label} className="text-center" data-id="element-325" data-mp="2u5spqhns5wgi">
                <Icon className="w-5 h-5 text-gold-metallic mx-auto mb-1.5" aria-hidden="true" data-id="element-326" data-mp="2u8evqcv0w3wm" />
                <dt className="text-[0.65rem] uppercase tracking-wider text-white/50" data-id="element-327" data-mp="2kf7u933el81t">
                  {label}
                </dt>
                <dd className="text-sm font-semibold leading-snug" data-id="element-328" data-mp="2fihb26waxwc6">{value}</dd>
              </div>)}
          </dl>

          <div className="w-full max-w-md mx-auto rounded-2xl bg-white p-4 md:p-6 shadow-2xl border-t-4 border-[#E5A82E]" data-id="element-329" data-mp="26ggkfsvkq8jh">
            <h2 className="text-base md:text-xl font-bold text-center text-dark mb-0.5" data-id="element-330" data-mp="21i6lr8an4pt4">
              Reserva tu lugar
            </h2>
            <p className="text-center text-xs text-gray-500 mb-3" data-id="element-331" data-mp="4olc9n8j0m60">
              Registro sin costo · Recibirás el enlace de acceso
            </p>

            <iframe src={`${GHL_FORM_HOST}/widget/form/${formId}`} style={{
            width: '100%',
            border: 'none',
            borderRadius: '4px',
            minHeight: '280px'
          }} id={`inline-${formId}`} data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-activation-type="alwaysActivated" data-deactivation-type="neverDeactivate" data-form-name="Clase FB Registration" data-height="280" data-layout-iframe-id={`inline-${formId}`} data-form-id={formId} title="Registro a la clase en vivo" data-id="element-332" data-mp="79t3qyvcerl6" />

            <p className="mt-2 text-center text-xs text-gray-500" data-id="element-333" data-mp="38pke2e36df7">
              Cupo limitado.
            </p>
          </div>
        </motion.section>
      </main>

      <MentorSection data-id="element-334" data-mp="1emlcscu04iod" />

      {/* FOOTER */}
      <footer className="relative z-10 w-full border-t border-white/10 bg-black/60 backdrop-blur-sm" data-id="element-335" data-mp="1eljp98vsu3pr">
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-center md:text-left" data-id="element-336" data-mp="1zrf235tq9z8s">
          <div data-id="element-337" data-mp="29goukmshhjxf">
            <p className="text-lg md:text-2xl font-bold" data-id="element-338" data-mp="29hgnfpup53k0">
              Bienes Raíces Academy
            </p>
            <p className="text-sm md:text-lg text-white/70" data-id="element-339" data-mp="24p75avztp7sa">
              Por Susana Sánchez G
            </p>
          </div>

          <div className="text-center leading-tight" data-id="element-340" data-mp="2ig28brnn4uny">
            <p className="text-base md:text-xl font-bold tracking-[0.15em] text-white" data-id="element-341" data-mp="2egl0wsonlng5">
              ACADEMIA
            </p>
            <p className="text-lg md:text-2xl font-extrabold tracking-wide text-gold-metallic" data-id="element-342" data-mp="2sehz1tira1wx">
              BIENES RAÍCES
            </p>
          </div>

          <p className="text-xs text-white/50 md:text-right" data-id="element-343" data-mp="y1jpxd8slbb6">
            Todos los Derechos Reservados
            <br data-id="element-344" data-mp="t2ypbcpmopm7" />
            2026
          </p>
        </div>
      </footer>
    </div>;
}